import React, { useEffect, useState } from 'react'
import { Card, Row, Col, Typography, Alert, Button, Spin, App } from 'antd'
import { getAuthTokens } from '@vacationtracker/shared/services/auth'
import axios from 'axios'
import { v4 as uuidv4 } from 'uuid'
import logger from '@vacationtracker/shared/functions/logger'
import { IADPUsersResponse } from '@vacationtracker/shared/types/adp'
import IntlMessages from '@vacationtracker/shared/components/utils/IntlMessages'
import { useIntl } from 'react-intl'
import { useAppSelector } from '../../../../store/hooks'
import { selectAuthCompanySlice } from '../../../../store/auth-company-slice'
import { PlatformEnum } from '@vacationtracker/shared/data/platforms'
import upperFirst from 'lodash/upperFirst'
import Api from '@vacationtracker/shared/services/api'
import { wait } from '@vacationtracker/shared/functions/wait'

const { Title, Paragraph, Text } = Typography

interface UsersStepProps {
  onImport: () => void
  onSkip: () => void
  onDataLoaded?: (data: IADPUsersResponse) => void
}

export const calculateNoOfUsersForImport = (data: IADPUsersResponse, platform: PlatformEnum): number => {
  if (platform === PlatformEnum.Email) {
    return data.seats < data.adp.total ? data.seats : data.adp.total
  }
  const adpMatched = data[platform as PlatformEnum]?.adpMatched
  return data.seats < adpMatched ? data.seats : adpMatched
}

export const UsersStep: React.FC<UsersStepProps> = ({ 
  onImport, 
  onSkip, 
  onDataLoaded,
}) => {

  const { notification } = App.useApp()
  const { formatMessage } = useIntl()
  const [loading, setLoading] = useState(true)
  const [importing, setImporting] = useState(false)
  const [noOfUsersForImport, setNoOfUsersForImport] = useState(0)
  const [data, setData] = useState<IADPUsersResponse | null>(null)
  const { authCompany } = useAppSelector(selectAuthCompanySlice)

  useEffect(() => {
    getAdpUsersInfo()
  }, [])

  const getAdpUsersInfo = async () => {
    try {
      const tokens = await getAuthTokens()
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/integrations/adp/users-stats`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokens?.idToken}`,
        },
        params: {
          platformToken: localStorage.getItem('vt-onboarding-platform-token'),
        },
      }) as {data: IADPUsersResponse}
      setData(response.data)
      setNoOfUsersForImport(calculateNoOfUsersForImport(response.data, authCompany?.platform as PlatformEnum))
      setLoading(false)
      onDataLoaded && onDataLoaded(response.data)
    } catch (error) {
      setLoading(false)
      setData(null)
      const id = uuidv4()
      notification.error({
        message: formatMessage({ id: 'notifications.error' }, { correlationId: id }),
        description: formatMessage({ id: 'onboarding.adp.usersStep.failed' }),
        duration: 100,
      })
      logger.error(id, error)
    }
  }

  const importAdpUsers = async () => {
    if (!data) return
    
    setImporting(true)
    try {
      const event = {
        eventType: 'SYNC_INTEGRATION_USERS',
        eventGroup: 'BULK_ACTIONS',
        integrationType: 'ADP',
        platformAccessToken: localStorage.getItem('vt-onboarding-platform-token'),
      }
      await Api.post('/core/event', event)
      await wait(2000)
      setImporting(false)
      onImport()
    } catch (error) {
      setImporting(false)
      const id = uuidv4()
      notification.error({
        message: formatMessage({ id: 'notifications.error' }, { correlationId: id }),
        description: formatMessage({ id: 'onboarding.adp.usersStep.importFailed' }),
      })
      logger.error(id, error)
    }
  }

  const handleImportClick = () => {
    importAdpUsers()
  }

  if (loading || !data) {
    return (
      <div className='users-step-loading-container'>
        <Spin size='large' className='users-step-spinner' />
        <div className='users-step-loading-text'>
          <IntlMessages id="onboarding.adp.usersStep.loading" />
        </div>
      </div>
    )
  }

  return (
    <div className='users-step-container'>
      <Title level={2} className='users-step-title'><IntlMessages id="users.importUsers" /></Title>
      <Paragraph className='users-step-description'>
        <IntlMessages id="onboarding.adp.usersStep.importDescription" />
      </Paragraph>
      <Card className='users-step-card' variant='outlined'>
        <Row gutter={32} justify='center'>
          <Col span={8} className='users-step-col'>
            <Title level={1} className='users-step-number'>{data.seats}</Title>
            <Text className='users-step-label'><IntlMessages id="onboarding.adp.usersStep.seatsAvailable" /></Text>
          </Col>
          <Col span={8} className='users-step-col'>
            {/* 1500 is max limit from our api response */}
            <Title level={1} className='users-step-number'>{data?.adp?.total}{data?.adp?.total === 1500 ? '+' : ''}</Title>
            <Text className='users-step-label'><IntlMessages id="onboarding.adp.usersStep.adpEmployees" /></Text>
          </Col>
          {authCompany?.platform !== PlatformEnum.Email && <Col span={8} className='users-step-col'>
            <Title level={1} className='users-step-number'>
              {data?.[authCompany?.platform as PlatformEnum]?.adpMatched}
              <span className='users-step__out-of'>
                <IntlMessages id="app.of" defaultMessage="out of" />
              </span>
              {data?.[authCompany?.platform as PlatformEnum]?.total}
            </Title>
            <Text className='users-step-label'><IntlMessages id="app.matched" />&nbsp;{upperFirst(authCompany?.platform)}&nbsp;<IntlMessages id="app.users" /></Text>
          </Col>}
        </Row>
      </Card>
      <Paragraph className='users-step-info'>
        <IntlMessages id="onboarding.adp.usersStep.importDescription2" values={{ seats: data.seats }} />
      </Paragraph>
      {data.seats >= data.adp.total && <Alert
        message={<IntlMessages id="onboarding.adp.usersStep.info4" 
          values={{ total: data.adp.total }} />
        }
        type='success'
        showIcon
        className='users-step-alert'
      />}
      {data.seats < data.adp.total && <Alert
        message={<IntlMessages id="onboarding.adp.usersStep.info3" 
          values={{ total: data.adp.total, seats: data.seats }} />
        }
        type='warning'
        showIcon
        className='users-step-alert'
      />}
      {authCompany?.platform !== PlatformEnum.Email && data.seats < data[authCompany?.platform as PlatformEnum]?.adpMatched && <Alert
        message={<IntlMessages id="onboarding.adp.usersStep.info1" 
          values={{ total: data[authCompany?.platform as PlatformEnum]?.adpMatched, platform: authCompany?.platform, seats: data.seats }} />
        }
        type='warning'
        showIcon
        className='users-step-alert'
      />}
      {
        authCompany?.platform !== PlatformEnum.Email &&
        data.adp.total > data[authCompany?.platform as PlatformEnum]?.adpMatched &&
        data[authCompany?.platform as PlatformEnum]?.adpMatched > 0 && 
      <Alert
        message={<IntlMessages id="onboarding.adp.usersStep.info2.1" 
          values={{ total: data[authCompany?.platform as PlatformEnum]?.adpMatched, platform: authCompany?.platform, seats: data.seats }} />
        }
        type='warning'
        showIcon
        className='users-step-alert'
      />}
      {
        authCompany?.platform !== PlatformEnum.Email &&
        data.adp.total > data[authCompany?.platform as PlatformEnum]?.adpMatched &&
        data[authCompany?.platform as PlatformEnum]?.adpMatched === 0 && 
      <Alert
        message={<IntlMessages id="onboarding.adp.usersStep.info2.2" 
          values={{ platform: authCompany?.platform, seats: data.seats }} />
        }
        type='warning'
        showIcon
        className='users-step-alert'
      />}
      <Row gutter={16} justify='start' className='users-step-actions'>
        <Col>
          <Button 
            type='primary' 
            size='large' 
            onClick={handleImportClick}
            loading={importing}
            disabled={importing || noOfUsersForImport < 1}
            className='users-step-import-btn'
          >
            <IntlMessages id="onboarding.adp.usersStep.importButton" values={{ total: noOfUsersForImport }} />
          </Button>
        </Col>
        <Col className='users-step-later-btn-col'>
          <Button type='link' size='large' onClick={onSkip} disabled={importing} className='users-step-later-btn'>
            <IntlMessages id="onboarding.adp.usersStep.skip" />
          </Button>
        </Col>
      </Row>
    </div>
  )
}
